import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Flex, Typography } from 'antd';
import isEqual from 'lodash/isEqual';

import { useUpsertWebhookConfig } from '../api';
import { IWebhookConfig } from '../types';
import { WebhookPayloadToggle } from './WebhookPayloadToggle';
import { scopes } from '@/config';
import useAppState from '@/hooks/useAppState';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

interface IWebhookUrlItemProps {
  config: IWebhookConfig;
  partnerId: string;
  staticConfig: string[];
  names: Record<string, string>;
}

const buildPayloads = (
  config: IWebhookConfig,
  staticConfig: string[]
): Record<string, boolean> =>
  staticConfig.reduce<Record<string, boolean>>(
    (acc, key) => ({ ...acc, [key]: Boolean(config[key]) }),
    {}
  );

export const WebhookUrlItem = ({
  config,
  partnerId,
  staticConfig,
  names,
}: IWebhookUrlItemProps) => {
  const addToast = useToasts((state) => state.addItem);
  const userScopes = useAppState((state) => state.scopes);
  const canManage = userScopes.includes(scopes.MANAGE_WEBHOOKS);

  const { mutateAsync: upsertConfig, isPending: isUpdating } =
    useUpsertWebhookConfig({ partnerId });

  const [localPayloads, setLocalPayloads] = useState<Record<string, boolean>>(
    () => buildPayloads(config, staticConfig)
  );

  const originalPayloads = useMemo(
    () => buildPayloads(config, staticConfig),
    [config, staticConfig]
  );
  const isDirty = !isEqual(localPayloads, originalPayloads);

  const handleToggle = useCallback((key: string, value: boolean) => {
    setLocalPayloads((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleReset = useCallback(() => {
    setLocalPayloads(buildPayloads(config, staticConfig));
  }, [config, staticConfig]);

  const handleUpdate = useCallback(async () => {
    try {
      await upsertConfig({
        configId: config.config_id,
        values: {
          ...config,
          ...localPayloads,
        },
      });
      addToast(
        { msg: 'Webhook updated', type: MessageType.Success },
        'root',
        {}
      );
    } catch (err) {
      addToast(
        {
          msg: err instanceof Error ? err.message : 'Update failed',
          type: MessageType.Error,
        },
        'root',
        {}
      );
    }
  }, [upsertConfig, config, localPayloads, addToast]);

  useEffect(() => {
    setLocalPayloads(buildPayloads(config, staticConfig));
  }, [config, staticConfig]);

  return (
    <>
      <Typography.Text
        strong
        style={{ marginBottom: 8, display: 'inline-block' }}
      >
        Active Payloads:
      </Typography.Text>
      {staticConfig.map((key) => (
        <WebhookPayloadToggle
          key={key}
          payloadKey={key}
          label={names[key] ?? key}
          checked={localPayloads[key] ?? false}
          onChange={handleToggle}
          disabled={!canManage}
        />
      ))}
      {isDirty && (
        <Flex justify="flex-end" gap={8} style={{ marginTop: 12 }}>
          <Button
            onClick={handleReset}
            data-testid={`webhook-cancel-${config.config_id}-button`}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={isUpdating}
            onClick={handleUpdate}
            data-testid={`webhook-update-${config.config_id}-button`}
          >
            Update
          </Button>
        </Flex>
      )}
    </>
  );
};
